import { Injectable, inject } from '@angular/core'
import { ResourceJSON, ResourceQueryJSON } from '@monorepo/next-lab'
import { IlsModelService } from './ils-model.service'
import { BaseStorageService } from '@monorepo/angular-common'

@Injectable({
  providedIn: 'root',
})
export class MemoryStorageService extends BaseStorageService {
  private readonly ilsModelService = inject(IlsModelService);

  private readonly resourcesMap = new Map<string, ResourceJSON>()

  override clear() {
    this.logDebug('clear')
    this.resourcesMap.clear()
    this.providerId = this.ilsModelService.ilsId
  }

  override async createResourcePromise(resource: ResourceJSON): Promise<ResourceJSON> {
    if (this.debug) {
      console.log('createResourcePromise', resource)
    }
    this.validateResource(resource, false)

    const clonedResource = this.cloneResource(resource)

    clonedResource.metadata.target.id = this.generateUUID()
    this.resourcesMap.set(clonedResource.metadata.target.id, clonedResource)
    return this.cloneResource(clonedResource)
  }

  override async deleteResourcePromise(resourceId: string): Promise<void> {
    if (this.debug) {
      console.log('deleteResourcePromise', resourceId)
    }
    this.resourcesMap.delete(resourceId)
  }

  override async queryResourcesPromise(query: ResourceQueryJSON, _fetchFromServer: boolean): Promise<ResourceJSON[]> {
    return this.queryResourcesFromCachePromise(
      query,
      () => this.resourcesMap.keys(),
      (id) => this.resourcesMap.get(id)!,
    )
  }

  override async readResourcePromise(resourceId: string, _fetchFromServer: boolean): Promise<ResourceJSON> {
    if (this.debug) {
      console.log('readResourcePromise', resourceId)
    }
    const resource = this.resourcesMap.get(resourceId)
    if (resource) {
      return this.cloneResource(resource)
    } else {
      throw new Error('Resource not found')
    }
  }

  override async updateResourcePromise(resource: ResourceJSON, _async: boolean | undefined): Promise<ResourceJSON> {
    if (this.debug) {
      console.log('updateResourcePromise', resource)
    }
    this.validateResource(resource, true)

    if (!this.resourcesMap.has(resource.metadata.target.id)) {
      throw new Error('Resource not found')
    }

    const clonedResource = this.cloneResource(resource)
    this.resourcesMap.set(clonedResource.metadata.target.id, clonedResource)
    return this.cloneResource(clonedResource)
  }

  override async resourceExistsPromise(resourceId: string, _fetchFromServer: boolean): Promise<boolean> {
    if (this.debug) {
      console.log('resourceExistsPromise', resourceId)
    }
    return this.resourcesMap.has(resourceId)
  }

  override getDebugLabel(): string {
    return `MemoryStorageService`
  }

  protected generateUUID(): string {
    let d = new Date().getTime()
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      const r = (d + Math.random() * 16) % 16 | 0
      d = Math.floor(d / 16)
      if (char === 'x') {
        return r.toString(16)
      } else {
        return ((r & 0x7) | 0x8).toString(16)
      }
    })
    return uuid
  }
}
